import axios from "axios";
import { STORAGE_KEYS } from "@/lib/storage";

/**
 * Smart backend URL resolution:
 *   • If REACT_APP_BACKEND_URL is set AND matches the current browser origin,
 *     use it as absolute. This is the "preview" / standard case.
 *   • If the build-time URL differs from where the user is browsing (e.g.
 *     production bundle was built with REACT_APP_BACKEND_URL=...emergent.host
 *     but the user opened the custom domain xeniaggr.com), we MUST fall back
 *     to same-origin "" so the cookie set on xeniaggr.com is sent back.
 *     Otherwise every authenticated request returns 401 → frontend crashes
 *     when .map() runs on undefined data.
 *   • Capacitor native shells override via window.__API_BASE__.
 */
function resolveBaseURL() {
  if (typeof window !== "undefined" && window.__API_BASE__) {
    return `${window.__API_BASE__}/api`;
  }
  const buildTime = process.env.REACT_APP_BACKEND_URL || "";
  if (typeof window === "undefined") return `${buildTime}/api`;
  try {
    if (!buildTime) return "/api";
    const buildOrigin = new URL(buildTime).origin;
    if (buildOrigin === window.location.origin) {
      return `${buildTime}/api`;
    }
    // Build URL points elsewhere than the page → use relative same-origin so cookies flow.
    return "/api";
  } catch (_) {
    return "/api";
  }
}

export const API = resolveBaseURL();

// withCredentials sends the httpOnly `access_token` cookie on every request.
const api = axios.create({ baseURL: API, withCredentials: true });

api.interceptors.request.use((config) => {
  // Active property scope (kept in localStorage — non-sensitive UI preference)
  const propId = localStorage.getItem(STORAGE_KEYS.propertyId);
  if (propId) config.headers["X-Property-Id"] = propId;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const headers = err?.response?.headers || {};
    const url = err?.config?.url || "";
    if (status === 401) {
      // Admin 2FA cookie scaduto o mancante: non spammare toast "Verifica 2FA richiesta",
      // ricarica il pannello admin così AdminLayout re-fetcha /admin/2fa/status e mostra
      // la schermata Verify2FA con la UI pulita.
      const needsAdmin2FA = headers["x-admin-2fa"] === "required";
      const onAdminRoute = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
      if (needsAdmin2FA && onAdminRoute && !url.includes("/admin/2fa/")) {
        window.location.assign("/admin");
        // Riject anyway so caller doesn't try to render undefined data.
        return Promise.reject(err);
      }
      // Normal 401: drop cached user so AuthContext flips to logged-out.
      localStorage.removeItem(STORAGE_KEYS.user);
    }
    return Promise.reject(err);
  }
);

export default api;
